import React from "react";
import * as styles from "@styles/ClientsPage.css";
import type { EmitOnAction, StateUpdate } from "@ps-bridge/capabilitiesTypes";
import type { Ok } from "@ps-bridge/ok";
import type { Client } from "@ps-bridge/models/core/dataContracts";
import * as Option from "@utils/Option";
import type { IOts, OptionTs } from "@ps-bridge/interop";
import EmptyView from "@view/EmptyView";

export type ClientsPanelToolbarPhase = "empty" | "emptyHidden" | "search";
export type ClientsPanelEmptyResultPhase = "hidden" | "visible";

type ClientsPanelProps = {
  clients: Client[];
  filteredClients: Client[];
  activeClientId: OptionTs<string>;
  clientQuery: string;
  clientQueryOptions: Client[];
  clientEmptyText: string;
  toolbarPhase: ClientsPanelToolbarPhase;
  emptyResultPhase: ClientsPanelEmptyResultPhase;
  onClientQueryChange: StateUpdate<string>;
  onCreateClient: () => Ok;
  onNavigateClient: EmitOnAction<string>;
  runIO: (effect: IOts<Ok>) => Ok;
  formatTime: (value: string) => string;
};

const ClientsPanel: React.FC<ClientsPanelProps> = ({
  clients: _clients,
  filteredClients,
  activeClientId,
  clientQuery,
  clientQueryOptions,
  clientEmptyText,
  toolbarPhase,
  emptyResultPhase,
  onClientQueryChange,
  onCreateClient,
  onNavigateClient,
  runIO,
  formatTime,
}) => {
  const toolbarView = {
    empty: (
      <div className={styles.emptyCreate}>
        <button className={styles.primaryButtonLarge} onClick={onCreateClient}>
          Add your first client +
        </button>
      </div>
    ),
    emptyHidden: <EmptyView />,
    search: (
      <div className={styles.toolbar}>
        <input
          className={styles.searchInput}
          placeholder="Search clients"
          value={clientQuery}
          onChange={(event) => runIO(onClientQueryChange(event.target.value))}
          list="client-search-options"
        />
        <datalist id="client-search-options">
          {clientQueryOptions.map((client) => (
            <option key={client.id} value={client.name} />
          ))}
        </datalist>
      </div>
    ),
  } as const satisfies Record<ClientsPanelToolbarPhase, React.ReactElement>;

  const emptyResultView = {
    hidden: <EmptyView />,
    visible: <p className={styles.emptyState}>{clientEmptyText}</p>,
  } as const satisfies Record<ClientsPanelEmptyResultPhase, React.ReactElement>;

  return (
    <div className={styles.panelBody}>
      {toolbarView[toolbarPhase]}
      <div className={styles.list}>
        {filteredClients.map((client) => (
          <button
            key={client.id}
            className={`${styles.listRow} ${
              Option.fold(activeClientId, () => false, (value) => value === client.id)
                ? styles.listRowActive
                : ""
            }`}
            onClick={() => runIO(onNavigateClient(client.id))}
          >
            <div>
              <span className={styles.listTitle}>{client.name}</span>
              <span className={styles.listMeta}>
                Created {formatTime(client.created_at)}
              </span>
            </div>
            <span className={styles.listChevron}>Open</span>
          </button>
        ))}
        {emptyResultView[emptyResultPhase]}
      </div>
    </div>
  );
};

export default ClientsPanel;
